import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
const YEARS       = [2023, 2024, 2025, 2026, 2027];
const DOW_TH      = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const DOW_FULL    = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

// ─── Pure helpers ──────────────────────────────────────────────────────────────
function fmt(n) {
  const x = parseFloat(n);
  return isNaN(x) ? '-' : x.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getDateRange(year, month, half) {
  const base = dayjs().year(year).month(month);
  if (half === 'first')  return { start: base.startOf('month').toISOString(), end: base.date(15).endOf('day').toISOString() };
  if (half === 'second') return { start: base.date(16).startOf('day').toISOString(), end: base.endOf('month').toISOString() };
  return { start: base.startOf('month').toISOString(), end: base.endOf('month').toISOString() };
}

function buildCalCell(checkin, empType, empSpd) {
  const late = (checkin.minutes_late ?? 0) > 0;
  const s    = late ? 'ส' : '';
  const er = checkin.effectiveRate || checkin.rate || 1;

  if (empType === 'FULLTIME') {
    const rStr = er > 1 ? `(×${er})` : '';
    const h = Math.round((checkin.hour_override?.hours ?? checkin.totalHour ?? 0) * 10) / 10;
    return h > 0 ? `${h}${rStr}${s}` : (late ? `0${s}` : null);
  }
  // DAILY: แสดง rate เฉพาะเมื่อ > 1 + คำนวณยอดเหมือน MergedPayrollTableV2
  const rStr = er > 1 ? `(×${er})` : '';
  const spd = empSpd ?? checkin.salary_per_day ?? 0;
  let amt;
  if (checkin.salary_override?.amount != null) {
    amt = checkin.salary_override.amount;
  } else {
    const citH = checkin.checkInTime ? parseInt(checkin.checkInTime.split(':')[0], 10) : 0;
    const displayH = citH >= 12 ? 4 : 8;
    amt = (displayH / 8) * er * spd;
  }
  return amt > 0
    ? `${Math.round(amt).toLocaleString('th-TH')}${rStr}${s}`
    : (late ? `0${s}` : null);
}

function buildOtCell(checkin) {
  const ot = checkin.otRequests;
  if (!ot || Array.isArray(ot) || !(ot.total_hours > 0)) return null;
  const rStr = ot.rate ? `(×${ot.rate})` : '';
  return `+${ot.total_hours}${rStr}`;
}

function isAdminEdited(checkin) {
  if (!checkin) return false;
  return !!(
    checkin.is_manual ||
    checkin.hour_override?.edited_by ||
    checkin.time_override?.edited_by ||
    checkin.salary_override?.edited_by
  );
}

// ─── Table style tokens ────────────────────────────────────────────────────────
const TH  = { border: '1px solid #bbb', padding: '5px 7px', whiteSpace: 'nowrap', textAlign: 'right', background: '#f0f0f0', fontSize: 12 };
const TD  = { border: '1px solid #ddd', padding: '4px 7px', whiteSpace: 'nowrap', fontSize: 12 };
const TF  = { border: '1px solid #bbb', padding: '5px 7px', whiteSpace: 'nowrap', fontWeight: 700, background: '#e8e8e8', fontSize: 12 };
const INP = { width: 72, padding: '2px 4px', fontSize: 11, textAlign: 'right', border: '1px solid #ccc', borderRadius: 2 };
const THC = { border: '1px solid #bbb', padding: '3px 4px', textAlign: 'center', whiteSpace: 'nowrap', fontSize: 11 };
const TDC = { border: '1px solid #e0e0e0', padding: '3px 5px', textAlign: 'right', fontSize: 11, whiteSpace: 'nowrap' };

// ─── Lightbox — ขยายรูปเต็มจอ ────────────────────────────────────────────────
function Lightbox({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.88)', zIndex: 999999,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{ color: '#ccc', fontSize: 12, marginBottom: 8 }}>{label} — คลิกที่ใดก็ได้เพื่อปิด</div>
      <img
        src={src}
        alt={label}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '92vw', maxHeight: '85vh',
          borderRadius: 6, boxShadow: '0 4px 32px rgba(0,0,0,0.6)',
          objectFit: 'contain',
        }}
      />
    </div>
  );
}

// ─── Detail Modal — แสดงเมื่อ double-click ────────────────────────────────────
function DetailModal({ data, onClose }) {
  const [lightbox, setLightbox] = React.useState(null); // { src, label }

  if (!data) return null;
  const { empName, dateLabel, checkin, empType, empSpd, loadingPhotos, photos } = data;

  const ot       = checkin.otRequests;
  const hasOt    = ot && !Array.isArray(ot) && ot.total_hours > 0;
  const edited   = isAdminEdited(checkin);
  const isDaily  = empType !== 'FULLTIME';
  const er       = checkin.effectiveRate || checkin.rate || 1;
  const spd      = empSpd ?? checkin.salary_per_day ?? 0;
  const effectiveH = checkin.hour_override?.hours ?? checkin.totalHour ?? 0;

  // คำนวณค่าแรงวันนี้ (เฉพาะ DAILY)
  let todayAmt = null;
  if (isDaily && spd > 0) {
    if (checkin.salary_override?.amount != null) {
      todayAmt = checkin.salary_override.amount;
    } else {
      const citH = checkin.checkInTime ? parseInt(checkin.checkInTime.split(':')[0], 10) : 0;
      const displayH = citH >= 12 ? 4 : 8;
      todayAmt = (displayH / 8) * er * spd;
    }
  }

  // OT effective hours
  const otRate = ot?.rate ?? null;
  const otEffH = ot?.total_hours != null && otRate != null
    ? parseFloat((ot.total_hours * otRate).toFixed(2))
    : (checkin.otEffectiveHours || null);

  const checkInPhoto  = photos?.checkInPhoto  || null;
  const checkOutPhoto = photos?.checkOutPhoto || null;

  const adminFields = [];
  if (checkin.is_manual)                      adminFields.push('Manual Entry');
  if (checkin.hour_override?.edited_by)       adminFields.push(`ชั่วโมง (${checkin.hour_override.edited_by})`);
  if (checkin.time_override?.edited_by)       adminFields.push(`เวลา (${checkin.time_override.edited_by})`);
  if (checkin.salary_override?.edited_by)     adminFields.push(`เงิน (${checkin.salary_override.edited_by})`);

  const otStatusLabel = ot?.status === 'APPROVE' ? '✓ อนุมัติแล้ว'
    : ot?.status === 'REJECT' ? '✗ ปฏิเสธ'
    : '⏳ รอดำเนินการ';
  const otStatusColor = ot?.status === 'APPROVE' ? '#2e7d32'
    : ot?.status === 'REJECT' ? '#c62828'
    : '#e65100';

  const photoSlot = (src, label) => src ? (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{label}</div>
      <div style={{ position: 'relative', cursor: 'zoom-in' }} onClick={() => setLightbox({ src, label })}>
        <img
          src={src}
          alt={label}
          style={{
            width: '100%', aspectRatio: '4/3', objectFit: 'cover',
            borderRadius: 4, border: '1px solid #ddd', display: 'block',
          }}
        />
        <div style={{
          position: 'absolute', bottom: 4, right: 4,
          background: 'rgba(0,0,0,0.45)', color: '#fff',
          fontSize: 10, padding: '2px 5px', borderRadius: 3,
        }}>
          ขยาย
        </div>
      </div>
    </div>
  ) : (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{label}</div>
      <div style={{
        width: '100%', aspectRatio: '4/3',
        background: '#eee', borderRadius: 4, border: '1px dashed #ccc',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: '#aaa',
      }}>
        ไม่มีรูป
      </div>
    </div>
  );

  return (
    <>
      {lightbox && <Lightbox src={lightbox.src} label={lightbox.label} onClose={() => setLightbox(null)} />}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: '#fff', borderRadius: 8, padding: 24, maxWidth: 520, width: '92%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>รายละเอียดการทำงาน</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{empName} — {dateLabel}</div>
            </div>
            <button
              onClick={onClose}
              style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#666', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>

          {/* Work section */}
          <div style={{ background: '#f5f5f5', borderRadius: 6, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>การทำงาน</div>
            <div style={{ display: 'flex', gap: 20, fontSize: 12, marginBottom: 8 }}>
              <div>
                <div style={{ color: '#777', fontSize: 11 }}>เช็คอิน</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: edited ? '#c62828' : '#1a237e' }}>
                  {checkin.checkInTime || '-'}
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: 11 }}>เช็คเอาท์</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a237e' }}>
                  {checkin.checkOutTime || '-'}
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: 11 }}>รวม</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: edited ? '#c62828' : '#1a237e' }}>
                  {effectiveH.toFixed(1)} ชม.
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: 11 }}>Rate</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: er > 1 ? '#e65100' : '#1a237e' }}>
                  ×{er}
                </div>
              </div>
              {todayAmt != null && (
                <div>
                  <div style={{ color: '#777', fontSize: 11 }}>ค่าแรงวันนี้</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#2e7d32' }}>
                    {Math.round(todayAmt).toLocaleString('th-TH')} ฿
                  </div>
                </div>
              )}
              {(checkin.minutes_late ?? 0) > 0 && (
                <div>
                  <div style={{ color: '#777', fontSize: 11 }}>สาย</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#c0392b' }}>
                    {checkin.minutes_late} นาที
                  </div>
                </div>
              )}
            </div>

            {edited && (
              <div style={{
                fontSize: 11, color: '#c62828', background: '#ffebee',
                padding: '5px 8px', borderRadius: 4, marginBottom: 8,
              }}>
                ⚠️ แก้ไขโดย Admin: {adminFields.join(', ')}
              </div>
            )}

            {/* Photos — horizontal */}
            {loadingPhotos ? (
              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>กำลังโหลดรูป...</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                {photoSlot(checkInPhoto,  'รูปเช็คอิน')}
                {photoSlot(checkOutPhoto, 'รูปเช็คเอาท์')}
              </div>
            )}
          </div>

        {/* OT section */}
        {hasOt ? (
          <div style={{
            background: '#fff8e1', borderRadius: 6, padding: 12,
            border: '1px solid #ffe082',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#e65100' }}>โอที (OT)</div>
            <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
              <div>
                <span style={{ color: '#777' }}>วันที่: </span>
                <span style={{ fontWeight: 600 }}>
                  {ot.date ? dayjs(ot.date).format('DD/MM/YYYY') : '-'}
                </span>
              </div>
              <div>
                <span style={{ color: '#777' }}>ช่วงเวลา: </span>
                <span style={{ fontWeight: 600 }}>
                  {ot.startTime ? dayjs(ot.startTime).format('HH:mm') : '-'}
                  {' – '}
                  {ot.endTime ? dayjs(ot.endTime).format('HH:mm') : '-'}
                </span>
              </div>
              <div>
                <span style={{ color: '#777' }}>ชั่วโมง: </span>
                <span style={{ fontWeight: 600 }}>{ot.total_hours ?? '-'} ชม.</span>
              </div>
              <div>
                <span style={{ color: '#777' }}>Rate OT: </span>
                <span style={{ fontWeight: 700, color: '#e65100' }}>
                  ×{otRate ?? '-'}
                </span>
              </div>
              {otEffH != null && (
                <div>
                  <span style={{ color: '#777' }}>ชม. effective: </span>
                  <span style={{ fontWeight: 600 }}>{otEffH} ชม.</span>
                </div>
              )}
              <div>
                <span style={{ color: '#777' }}>ยอด: </span>
                <span style={{ fontWeight: 600 }}>
                  {(ot.total_price ?? 0).toLocaleString('th-TH')} ฿
                </span>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <span style={{ color: '#777' }}>สถานะ: </span>
                <span style={{ fontWeight: 700, color: otStatusColor }}>{otStatusLabel}</span>
              </div>
              {ot.description && (
                <div style={{ gridColumn: '1/-1', color: '#555', fontSize: 11 }}>
                  หมายเหตุ: {ot.description}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: '8px 0' }}>
            ไม่มีข้อมูล OT วันนี้
          </div>
        )}
        </div>
      </div>
    </>
  );
}

// ─── Memoized calendar row ──────────────────────────────────────────────────────
const CalRow = React.memo(function CalRow({ emp, colBgs, isEven, rowNum, onDblClick }) {
  const rowBg = isEven ? '#fff' : '#f7f7f7';
  return (
    <tr>
      <td style={{ ...TDC, textAlign: 'center', width: 28, background: rowBg, color: '#888' }}>{rowNum}</td>
      <td style={{ ...TDC, textAlign: 'left', minWidth: 130, background: rowBg, fontWeight: 500 }}>
        <div style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.name}</div>
      </td>
      {emp.days.map((cell, i) => {
        const ot      = emp.otDays[i];
        const edited  = isAdminEdited(emp.rawCheckins[i]);
        const isLate  = cell?.includes('ส');
        return (
          <td
            key={i}
            onDoubleClick={() => cell && onDblClick && onDblClick(i)}
            title={cell ? 'ดับเบิ้ลคลิกเพื่อดูรายละเอียด' : undefined}
            style={{
              ...TDC, minWidth: 36,
              background: cell
                ? (colBgs[i] ? '#ffe8e8' : '#edfaf1')
                : (colBgs[i] ? '#fff5f5' : rowBg),
              cursor: cell ? 'pointer' : undefined,
            }}
          >
            <div style={{ color: edited ? '#c62828' : (isLate ? '#c0392b' : undefined) }}>
              {cell ?? '-'}
            </div>
            {ot && <div style={{ fontSize: 9, color: '#e65100', fontWeight: 600 }}>{ot}</div>}
          </td>
        );
      })}
      <td style={{ ...TDC, fontWeight: 700, minWidth: 60, background: '#ebebeb' }}>
        {emp.empType === 'FULLTIME'
          ? `${emp.total}`
          : emp.total.toLocaleString('th-TH', { maximumFractionDigits: 0 })}
      </td>
      <td style={{ ...TDC, fontWeight: 700, minWidth: 55, background: '#fff3e0', color: '#e65100', textAlign: 'center' }}>
        {emp.totalOtHour > 0 ? `${emp.totalOtHour}` : '-'}
      </td>
      <td style={{ ...TDC, fontWeight: 700, minWidth: 72, background: '#fff3e0', color: '#e65100' }}>
        {emp.totalOtAmount > 0
          ? emp.totalOtAmount.toLocaleString('th-TH', { maximumFractionDigits: 0 })
          : '-'}
      </td>
      <td style={{ ...TDC, fontWeight: 700, minWidth: 40, background: '#fce4ec', color: '#c62828', textAlign: 'center' }}>
        {emp.absentDays > 0 ? emp.absentDays : '-'}
      </td>
      <td style={{ ...TDC, fontWeight: 700, minWidth: 40, background: '#fff8e1', color: '#f57f17', textAlign: 'center' }}>
        {emp.lateDays > 0 ? emp.lateDays : '-'}
      </td>
      <td style={{ ...TDC, fontWeight: 700, minWidth: 60, background: '#e8f5e9', color: '#2e7d32' }}>
        {emp.sso > 0 ? emp.sso.toLocaleString('th-TH') : '-'}
      </td>
    </tr>
  );
});

// ─── Main Component ────────────────────────────────────────────────────────────
export default function HrPayrollAdmin() {
  const dispatch = useDispatch();
  const me               = useSelector((s) => s.me);
  const timestampPayroll = useSelector((s) => s.timestampPayroll);
  const manualWork       = useSelector((s) => s.manualWork);
  const payrollAdj       = useSelector((s) => s.payrollAdj);

  const [month,      setMonth]      = useState(dayjs().month());
  const [year,       setYear]       = useState(dayjs().year());
  const [typeSelect, setTypeSelect] = useState('DAILY');
  const [halfMonth,  setHalfMonth]  = useState('all');
  const [view,       setView]       = useState('summary'); // 'summary' | 'calendar'

  const [loading,     setLoading]     = useState(false);
  const [adjEdits,    setAdjEdits]    = useState({});
  const [saving,      setSaving]      = useState({});
  const [detailModal, setDetailModal] = useState(null); // { empName, dateLabel, checkin }

  // ── Summary fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange(year, month, halfMonth);
    try {
      await Promise.all([
        dispatch(actions.timestampPayrollFetchBoth({ dateStart: start, dateEnd: end, typeSelect })),
        dispatch(actions.manualWorkAll({ dateStart: start, dateEnd: end })),
        dispatch(actions.payrollAdjAll({ dateStart: start, dateEnd: end })),
      ]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, month, year, typeSelect, halfMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // init adjEdits จาก payrollAdj ที่โหลดมา
  useEffect(() => {
    const next = {};
    (payrollAdj?.rows || []).forEach((a) => {
      next[String(a.employee_id)] = {
        revenue:       { ...(a.revenue || {}) },
        expenses:      { ...(a.expenses || {}) },
        manual_fields: [...(a.manual_fields || [])],
        note:          a.note || '',
      };
    });
    setAdjEdits(next);
  }, [payrollAdj]);

  // ── manualWork per employee ──
  const manualByEmp = useMemo(() => {
    const map = {};
    (manualWork?.rows || []).forEach((e) => {
      const k = String(e.employee_id);
      if (!map[k]) map[k] = { count: 0, amount: 0, otAmount: 0 };
      map[k].count    += 1;
      map[k].amount   += (e.amount || 0) + (e.ot_amount || 0) + (e.allowance_amount || 0);
      map[k].otAmount += (e.ot_amount || 0);
    });
    return map;
  }, [manualWork]);

  // ── Calendar: day headers ──
  const dayHeaders = useMemo(() => {
    const n = dayjs().year(year).month(month).daysInMonth();
    return Array.from({ length: n }, (_, i) => {
      const d   = i + 1;
      const dow = dayjs().year(year).month(month).date(d).day();
      return { d, label: DOW_TH[dow], isWeekend: dow === 0 };
    });
  }, [year, month]);

  const colBgs = useMemo(() => dayHeaders.map((h) => h.isWeekend), [dayHeaders]);

  // ── Calendar: build grid ──
  const calData = useMemo(() => {
    const payrollRows = timestampPayroll?.rows || [];
    if (!payrollRows.length) return [];
    const n = dayHeaders.length;

    return payrollRows
      .map((emp) => {
        const days        = Array.from({ length: n }, () => null);
        const otDays      = Array.from({ length: n }, () => null);
        const rawCheckins = Array.from({ length: n }, () => null); // เก็บ checkin raw ต่อวัน
        const seen        = new Set();
        (emp.checkins || []).forEach((checkin) => {
          if (!checkin.checkIn) return;
          const dayNum = dayjs(checkin.checkIn).date();
          if (dayNum >= 1 && dayNum <= n && !seen.has(dayNum)) {
            seen.add(dayNum);
            days[dayNum - 1]        = buildCalCell(checkin, emp.emp_type, emp.salary_per_day);
            otDays[dayNum - 1]      = buildOtCell(checkin);
            rawCheckins[dayNum - 1] = checkin;
          }
        });
        const rangeStart = halfMonth === 'second' ? 16 : 1;
        let rangeEnd = halfMonth === 'first' ? 15 : n;
        // นับขาดถึงวันปัจจุบันเท่านั้น ถ้าเป็นเดือน/ปีปัจจุบัน
        const _today = dayjs();
        if (_today.year() === year && _today.month() === month) {
          rangeEnd = Math.min(rangeEnd, _today.date());
        }
        let absentDays = 0;
        for (let i = rangeStart - 1; i < rangeEnd; i++) {
          if (!dayHeaders[i].isWeekend && days[i] === null) absentDays++;
        }
        const lateDays = (emp.checkins || []).filter((c) => (c.minutes_late ?? 0) > 0).length;
        const total = emp.emp_type === 'FULLTIME'
          ? Math.round((emp.totalHour || 0) * 10) / 10
          : Math.round((emp.totalAmount || 0) * 10) / 10;
        return {
          empId: String(emp.employee_id), name: emp.name, empType: emp.emp_type,
          salary_per_day: emp.salary_per_day || 0,
          days, otDays, rawCheckins, total,
          totalOtHour:   Math.round((emp.totalOtHour   || 0) * 10) / 10,
          totalOtAmount: Math.round(emp.totalOtAmount  || 0),
          sso:           Math.round(emp.expenses?.sso   || 0),
          absentDays, lateDays,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'th'));
  }, [timestampPayroll, dayHeaders, halfMonth]);

  // ── Permission check ──
  const roles    = me?.userData?.system_role;
  const rolesArr = Array.isArray(roles) ? roles : (roles ? [roles] : []);
  const isAdmin  = rolesArr.some((r) => !r?.allowed_menus?.length)
    || me?.userData?.permissions?.admin
    || me?.userData?.permissions?.hr_management;

  if (me?.isLoading) return <div style={{ padding: 32, color: '#888' }}>กำลังโหลดข้อมูลผู้ใช้...</div>;
  if (!isAdmin)       return <div style={{ padding: 32 }}>ไม่มีสิทธิ์เข้าถึงหน้านี้</div>;

  // ── Summary helpers ──
  const rows = timestampPayroll?.rows || [];

  const setAdjField = (empId, cat, field, val) => {
    setAdjEdits((prev) => {
      const cur = prev[empId] || { revenue: {}, expenses: {}, manual_fields: [], note: '' };
      const mf  = new Set(cur.manual_fields || []);
      if (val !== '' && val !== null && val !== undefined) mf.add(field);
      else mf.delete(field);
      return { ...prev, [empId]: { ...cur, [cat]: { ...(cur[cat] || {}), [field]: val }, manual_fields: [...mf] } };
    });
  };

  const handleSave = async (empId) => {
    setSaving((s) => ({ ...s, [empId]: true }));
    const { start, end } = getDateRange(year, month, halfMonth);
    const edit = adjEdits[empId] || {};
    try {
      await dispatch(actions.payrollAdjUpsert({
        employee_id: empId, dateStart: start, dateEnd: end,
        revenue: edit.revenue || {}, expenses: edit.expenses || {},
        manual_fields: edit.manual_fields || [], note: edit.note || null,
      }));
    } catch { /* handled by action */ }
    setSaving((s) => ({ ...s, [empId]: false }));
  };

  const calcMerged = (row) => {
    const empId = String(row.employee_id);
    const edit = adjEdits[empId] || {};
    const mf   = edit.manual_fields || [];
    const pick = (cat, field, src) => mf.includes(field) ? (edit[cat]?.[field] ?? src) : src;
    const salary   = parseFloat(pick('revenue', 'salary',          row.revenue?.salary))          || 0;
    // รวม OT ที่ HR เพิ่ม manual (ผ่าน ManualWorkEntry.ot_amount) เข้ากับ OT อนุมัติจาก OtRequest
    const manualOt = manualByEmp[empId]?.otAmount || 0;
    const overtime = parseFloat(pick('revenue', 'overtime', (row.revenue?.overtime || 0) + manualOt)) || 0;
    const allow    = parseFloat(pick('revenue', 'allowances',      row.revenue?.allowances))      || 0;
    const field    = parseFloat(pick('revenue', 'field_allowance', row.revenue?.field_allowance)) || 0;
    const bonus    = parseFloat(pick('revenue', 'bonus',           row.revenue?.bonus))           || 0;
    const sso      = parseFloat(pick('expenses','sso',             row.expenses?.sso))            || 0;
    const tax      = parseFloat(pick('expenses','tax',             row.expenses?.tax))            || 0;
    const late     = parseFloat(pick('expenses','late',            row.expenses?.late))           || 0;
    const other    = parseFloat(pick('expenses','other',           row.expenses?.other))          || 0;
    return { salary, overtime, allow, field, bonus, sso, tax, late, other,
      income: salary + overtime + allow + field + bonus,
      deduct: sso + tax + late + other,
      net:    salary + overtime + allow + field + bonus - sso - tax - late - other };
  };

  const totals = rows.reduce((acc, row) => {
    const r = calcMerged(row);
    return { salary: acc.salary + r.salary, overtime: acc.overtime + r.overtime,
      allow: acc.allow + r.allow, sso: acc.sso + r.sso, tax: acc.tax + r.tax, net: acc.net + r.net };
  }, { salary: 0, overtime: 0, allow: 0, sso: 0, tax: 0, net: 0 });

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '16px 8px', fontFamily: 'sans-serif', fontSize: 13 }}>

      {/* Detail Modal */}
      <DetailModal data={detailModal} onClose={() => setDetailModal(null)} />

      {/* ── Controls ── */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: '0 0 10px 0', fontSize: 18 }}>สรุปเงินเดือน — Admin</h2>

        {/* Selectors */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <label>เดือน</label>
          <select value={month} onChange={(e) => setMonth(+e.target.value)} style={{ padding: '4px 6px' }}>
            {MONTH_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <label>ปี</label>
          <select value={year} onChange={(e) => setYear(+e.target.value)} style={{ padding: '4px 6px' }}>
            {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
          </select>
          <label>ช่วง</label>
          <select value={halfMonth} onChange={(e) => setHalfMonth(e.target.value)} style={{ padding: '4px 6px' }}>
            <option value="all">ทั้งเดือน</option>
            <option value="first">1–15</option>
            <option value="second">16–31</option>
          </select>
          <label>ประเภท</label>
          <select value={typeSelect} onChange={(e) => setTypeSelect(e.target.value)} style={{ padding: '4px 6px' }}>
            <option value="DAILY">รายวัน</option>
            <option value="FULLTIME">รายเดือน</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            style={{ padding: '4px 14px', cursor: 'pointer' }}
          >
            {loading ? 'โหลด...' : 'โหลด'}
          </button>
        </div>

        {/* View tabs */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['summary', 'สรุปรายเดือน'], ['calendar', 'ปฏิทินรายวัน']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '5px 16px', fontSize: 12, border: '1px solid #bbb', borderRadius: '4px 4px 0 0',
                cursor: 'pointer', fontWeight: view === v ? 700 : 400,
                background: view === v ? '#1976d2' : '#f5f5f5',
                color: view === v ? '#fff' : '#555',
                borderBottom: view === v ? '1px solid #1976d2' : '1px solid #bbb',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════
           VIEW: SUMMARY TABLE
          ════════════════════════════════════════════════════════════ */}
      {view === 'summary' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'center' }} rowSpan={2}>#</th>
                <th style={{ ...TH, textAlign: 'left', minWidth: 140 }} rowSpan={2}>ชื่อ-สกุล</th>
                <th style={{ ...TH, textAlign: 'center' }} rowSpan={2}>ประเภท</th>
                <th style={TH} rowSpan={2}>วันทำ</th>
                <th style={TH} rowSpan={2}>Extra</th>
                <th style={{ ...TH, borderLeft: '2px solid #888', textAlign: 'center' }} colSpan={5}>รายได้ (แก้ได้)</th>
                <th style={{ ...TH, borderLeft: '2px solid #888', textAlign: 'center' }} colSpan={4}>หัก (แก้ได้)</th>
                <th style={{ ...TH, borderLeft: '2px solid #555', minWidth: 90 }} rowSpan={2}>หมายเหตุ</th>
                <th style={{ ...TH, borderLeft: '2px solid #555' }} rowSpan={2}>สุทธิ</th>
                <th style={TH} rowSpan={2}></th>
              </tr>
              <tr>
                <th style={{ ...TH, borderLeft: '2px solid #888' }}>เงินเดือน</th>
                <th style={TH}>OT</th><th style={TH}>เบี้ยเลี้ยง</th>
                <th style={TH}>เบี้ยสนาม</th><th style={TH}>โบนัส</th>
                <th style={{ ...TH, borderLeft: '2px solid #888' }}>ประกันสังคม</th>
                <th style={TH}>ภาษี</th><th style={TH}>หักสาย</th><th style={TH}>หักอื่น</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={17} style={{ ...TD, textAlign: 'center', padding: 32, color: '#888' }}>กำลังโหลด...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={17} style={{ ...TD, textAlign: 'center', padding: 32, color: '#888' }}>ไม่มีข้อมูล</td></tr>
              )}
              {!loading && rows.map((row, i) => {
                const empId  = String(row.employee_id);
                const r      = calcMerged(row);
                const edit   = adjEdits[empId] || {};
                const mf     = edit.manual_fields || [];
                const extra  = manualByEmp[empId];
                const hasAdj = mf.length > 0 || edit.note;
                const adjBg  = (f) => mf.includes(f) ? '#fffde7' : undefined;
                const valOf  = (cat, f, fb) => edit[cat]?.[f] != null ? edit[cat][f] : (fb ?? '');
                return (
                  <tr key={empId} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    <td style={{ ...TD, textAlign: 'center' }}>{i + 1}</td>
                    <td style={TD}>
                      {row.name}
                      {hasAdj && <span style={{ color: '#e67e22', marginLeft: 4, fontSize: 10 }}>●</span>}
                    </td>
                    <td style={{ ...TD, textAlign: 'center' }}>{row.emp_type === 'FULLTIME' ? 'ด.' : 'ว.'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{row.totalWork ?? '-'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: extra?.count ? '#1976d2' : '#ccc' }}>
                      {extra ? `${extra.count}ว.` : '-'}
                      {extra?.otAmount > 0 && (
                        <div style={{ fontSize: 10, color: '#e65100', fontWeight: 600 }}>
                          +OT ฿{Math.round(extra.otAmount).toLocaleString('th-TH')}
                        </div>
                      )}
                    </td>
                    {/* รายได้ */}
                    {[['revenue','salary',row.revenue?.salary],
                      ['revenue','overtime', (row.revenue?.overtime || 0) + (manualByEmp[empId]?.otAmount || 0)],
                      ['revenue','allowances',row.revenue?.allowances],['revenue','field_allowance',row.revenue?.field_allowance],
                      ['revenue','bonus',row.revenue?.bonus]].map(([cat, f, fb], fi) => (
                      <td key={f} style={{ ...TD, background: adjBg(f), ...(fi === 0 ? { borderLeft: '2px solid #888' } : {}) }}>
                        <input style={INP} type="number" value={valOf(cat, f, fb)}
                          onChange={(e) => setAdjField(empId, cat, f, e.target.value)} />
                      </td>
                    ))}
                    {/* หัก */}
                    {[['expenses','sso',row.expenses?.sso],['expenses','tax',row.expenses?.tax],
                      ['expenses','late',row.expenses?.late],['expenses','other',row.expenses?.other]].map(([cat, f, fb], fi) => (
                      <td key={f} style={{ ...TD, background: adjBg(f), ...(fi === 0 ? { borderLeft: '2px solid #888' } : {}) }}>
                        <input style={INP} type="number" value={valOf(cat, f, fb)}
                          onChange={(e) => setAdjField(empId, cat, f, e.target.value)} />
                      </td>
                    ))}
                    {/* หมายเหตุ */}
                    <td style={{ ...TD, borderLeft: '2px solid #555' }}>
                      <input style={{ ...INP, width: 100, textAlign: 'left' }} type="text"
                        value={edit.note ?? ''}
                        onChange={(e) => setAdjEdits((prev) => ({
                          ...prev,
                          [empId]: { ...(prev[empId] || { revenue: {}, expenses: {}, manual_fields: [] }), note: e.target.value },
                        }))} />
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600, borderLeft: '2px solid #555' }}>{fmt(r.net)}</td>
                    <td style={TD}>
                      <button onClick={() => handleSave(empId)} disabled={saving[empId]}
                        style={{ padding: '2px 10px', fontSize: 11, border: 'none', borderRadius: 3,
                          cursor: saving[empId] ? 'not-allowed' : 'pointer',
                          background: hasAdj ? '#1976d2' : '#aaa', color: '#fff' }}>
                        {saving[empId] ? '...' : 'บันทึก'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ ...TF, textAlign: 'center' }}>รวม {rows.length} คน</td>
                  <td style={{ ...TF, textAlign: 'right', borderLeft: '2px solid #888' }}>{fmt(totals.salary)}</td>
                  <td style={{ ...TF, textAlign: 'right' }}>{fmt(totals.overtime)}</td>
                  <td style={{ ...TF, textAlign: 'right' }}>{fmt(totals.allow)}</td>
                  <td style={TF}>-</td><td style={TF}>-</td>
                  <td style={{ ...TF, textAlign: 'right', borderLeft: '2px solid #888' }}>{fmt(totals.sso)}</td>
                  <td style={{ ...TF, textAlign: 'right' }}>{fmt(totals.tax)}</td>
                  <td style={TF}>-</td><td style={TF}>-</td>
                  <td colSpan={2} style={{ ...TF, borderLeft: '2px solid #555' }}></td>
                  <td style={{ ...TF, textAlign: 'right', borderLeft: '2px solid #555' }}>{fmt(totals.net)}</td>
                  <td style={TF}></td>
                </tr>
              </tfoot>
            )}
          </table>
          <p style={{ marginTop: 8, color: '#888', fontSize: 11 }}>
            * สีเหลือง = ค่าถูกปรับแก้ &nbsp;|&nbsp; ● = มีการปรับในแถวนี้ &nbsp;|&nbsp; Extra = วันจาก manualWork
          </p>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
           VIEW: CALENDAR (employee × day)
          ════════════════════════════════════════════════════════════ */}
      {view === 'calendar' && (
        <div>
          {loading ? (
            <div style={{ padding: 32, color: '#888' }}>กำลังโหลด...</div>
          ) : (
            <>
              {calData.length > 0 && (
                <p style={{ margin: '0 0 6px', fontSize: 11, color: '#888' }}>
                  พนักงาน {calData.length} คน &nbsp;|&nbsp;
                  <span style={{ color: '#c0392b' }}>สีแดง</span> = วันหยุด &nbsp;|&nbsp;
                  <span style={{ color: '#c0392b' }}>ส</span> = มาสาย &nbsp;|&nbsp;
                  <span style={{ color: '#c62828', fontWeight: 700 }}>ตัวแดง</span> = Admin แก้ไข &nbsp;|&nbsp;
                  ดับเบิ้ลคลิกเพื่อดูรายละเอียด
                </p>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ ...THC, width: 28, background: '#e8eaf6' }}>#</th>
                      <th style={{ ...THC, textAlign: 'left', minWidth: 130, background: '#e8eaf6', fontSize: 12 }}>
                        พนักงาน
                      </th>
                      {dayHeaders.map((h) => (
                        <th key={h.d} style={{
                          ...THC, minWidth: 38,
                          background: h.isWeekend ? '#ffd6d6' : '#e8eaf6',
                          color: h.isWeekend ? '#c0392b' : '#333',
                        }}>
                          <div>{h.d}</div>
                          <div style={{ fontSize: 9, color: h.isWeekend ? '#c0392b' : '#888' }}>{h.label}</div>
                        </th>
                      ))}
                      <th style={{ ...THC, minWidth: 60, background: '#c5cae9' }}>รวม</th>
                      <th style={{ ...THC, minWidth: 55, background: '#ffe0b2', color: '#e65100' }}>
                        <div>OT</div><div style={{ fontSize: 9 }}>(ชม.)</div>
                      </th>
                      <th style={{ ...THC, minWidth: 72, background: '#ffe0b2', color: '#e65100' }}>
                        <div>OT</div><div style={{ fontSize: 9 }}>(บาท)</div>
                      </th>
                      <th style={{ ...THC, minWidth: 40, background: '#fce4ec', color: '#c62828' }}>ขาด</th>
                      <th style={{ ...THC, minWidth: 40, background: '#fff8e1', color: '#f57f17' }}>สาย</th>
                      <th style={{ ...THC, minWidth: 60, background: '#c8e6c9', color: '#2e7d32' }}>
                        <div>ปกส.</div><div style={{ fontSize: 9 }}>(บาท)</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {calData.length === 0 ? (
                      <tr>
                        <td colSpan={dayHeaders.length + 6}
                          style={{ ...TDC, textAlign: 'center', padding: 32, color: '#888' }}>
                          ไม่มีข้อมูล — ลองเปลี่ยนเดือนหรือประเภทพนักงาน
                        </td>
                      </tr>
                    ) : (
                      calData.map((emp, i) => (
                        <CalRow
                          key={emp.empId}
                          emp={emp}
                          colBgs={colBgs}
                          isEven={i % 2 === 0}
                          rowNum={i + 1}
                          onDblClick={(dayIdx) => {
                            const checkin = emp.rawCheckins[dayIdx];
                            if (!checkin) return;
                            const d   = dayHeaders[dayIdx];
                            const dow = dayjs().year(year).month(month).date(d.d).day();
                            // เปิด modal ทันที แต่รูปยังโหลดอยู่
                            setDetailModal({
                              empName:       emp.name,
                              dateLabel:     `วัน${DOW_FULL[dow]}ที่ ${d.d} ${MONTH_NAMES[month]} ${year + 543}`,
                              checkin,
                              empType:       emp.empType,
                              empSpd:        emp.salary_per_day,
                              loadingPhotos: true,
                              photos:        null,
                            });
                            // Lazy-fetch รูป + full timestamp data
                            const tsId = checkin._id;
                            api.get(`${process.env.REACT_APP_API_URL}/timestamp/${tsId}`)
                              .then(({ data: ts }) => {
                                setDetailModal((prev) =>
                                  prev && String(prev.checkin._id) === String(tsId)
                                    ? {
                                        ...prev,
                                        loadingPhotos: false,
                                        photos: {
                                          checkInPhoto:  ts.image?.url  || null,
                                          checkOutPhoto: ts.image_out?.url || null,
                                        },
                                      }
                                    : prev
                                );
                              })
                              .catch(() => {
                                setDetailModal((prev) =>
                                  prev && String(prev.checkin._id) === String(tsId)
                                    ? { ...prev, loadingPhotos: false }
                                    : prev
                                );
                              });
                          }}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
